import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../App';
import { toast } from 'sonner';
import { Sparkles, X, Send } from 'lucide-react';

export function AIAdvisor({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await api.post('/api/advisor/chat', { message: userMessage, session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      toast.error('Failed to get AI response');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chat-container animate-slide-in" data-testid="ai-advisor-chat">
      <div className="bg-brand-navy p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Benchmark PH AI Advisor</h3>
            <p className="text-slate-400 text-xs">Powered by SusTek.Ai using Gemini 3 Flash LLM</p>
          </div>
        </div>
        <button 
          onClick={onClose}
          className="text-white/80 hover:text-white transition-colors"
          data-testid="close-advisor"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="h-80 overflow-y-auto p-4 space-y-4 bg-slate-50">
        {messages.length === 0 && (
          <div className="text-center text-slate-500 py-8">
            <Sparkles className="w-12 h-12 text-accent/30 mx-auto mb-3" />
            <p className="text-sm">Ask me anything about ESG topics!</p>
            <p className="text-xs mt-2">I can help explain questions or provide guidance.</p>
          </div>
        )}
        {messages.map((msg, idx) => (
          <div key={idx} className={`chat-message ${msg.role}`} data-testid={`chat-message-${idx}`}>
            {msg.content}
          </div>
        ))}
        {loading && (
          <div className="chat-message assistant">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={sendMessage} className="p-4 border-t bg-white">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about ESG topics..."
            className="flex-1 h-10 px-4 rounded-full border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 text-sm"
            data-testid="advisor-input"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="w-10 h-10 bg-accent text-primary rounded-full flex items-center justify-center hover:bg-accent/90 disabled:opacity-50 transition-all"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}
